import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr, INR, calcEMI } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const LOAN_TYPES = ["Personal", "Business", "Home", "Car", "Gold", "Education", "Property"];

export default function Apply() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    loan_type: params.get("loan_type") || "Personal",
    amount: 500000, tenure_months: 36, monthly_income: "", city: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get("/loan-products").then(({data}) => setProducts(data)); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedProduct = products.find(p => p.name.toLowerCase().startsWith(form.loan_type.toLowerCase()));
  const rate = selectedProduct?.interest_rate_from || 10.5;
  const emi = calcEMI(+form.amount || 0, rate, +form.tenure_months || 12);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.loan_type || !form.amount) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: +form.amount, tenure_months: +form.tenure_months, monthly_income: form.monthly_income ? +form.monthly_income : null };
      const { data } = await api.post("/leads", payload);
      toast.success("Application submitted! We'll reach out within 24 hours.");
      setSuccess(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div data-testid="apply-success" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 size={64} className="text-[#D4AF37] mx-auto"/>
        <h1 className="mt-6 font-display text-4xl text-white">Application Received</h1>
        <p className="mt-4 text-white/70">Reference ID: <span className="text-[#D4AF37] font-mono">{success.id.slice(0,8).toUpperCase()}</span></p>
        <p className="mt-2 text-white/60">Our team will call you on <span className="text-white">{success.phone}</span> within 24 hours.</p>
        <div className="mt-10 flex justify-center gap-3">
          <button onClick={() => nav("/")} className="sk-btn-ghost">Back to Home</button>
          <button onClick={() => nav("/register")} className="sk-btn-gold">Track my application</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="sk-badge sk-badge-gold">Apply now</div>
          <h1 className="mt-4 font-display text-5xl text-white leading-tight">Get funded in <span className="gold italic">24 hours</span></h1>
          <p className="mt-4 text-white/60">Fill this quick form — our loan expert will call you shortly.</p>

          <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5">
            <FormField label="Full name*" required>
              <input data-testid="apply-name" required value={form.name} onChange={(e)=>setField("name", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Phone*">
              <input data-testid="apply-phone" required type="tel" value={form.phone} onChange={(e)=>setField("phone", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Email">
              <input data-testid="apply-email" type="email" value={form.email} onChange={(e)=>setField("email", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="City">
              <input data-testid="apply-city" value={form.city} onChange={(e)=>setField("city", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Loan type*">
              <select data-testid="apply-loan-type" value={form.loan_type} onChange={(e)=>setField("loan_type", e.target.value)} className="sk-input">
                {LOAN_TYPES.map(t => <option key={t} value={t}>{t} Loan</option>)}
              </select>
            </FormField>
            <FormField label="Loan amount (₹)*">
              <input data-testid="apply-amount" required type="number" min="10000" value={form.amount} onChange={(e)=>setField("amount", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Tenure (months)">
              <input data-testid="apply-tenure" type="number" value={form.tenure_months} onChange={(e)=>setField("tenure_months", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Monthly income (₹)">
              <input data-testid="apply-income" type="number" value={form.monthly_income} onChange={(e)=>setField("monthly_income", e.target.value)} className="sk-input"/>
            </FormField>
            <FormField label="Anything else?" full>
              <textarea data-testid="apply-message" rows="3" value={form.message} onChange={(e)=>setField("message", e.target.value)} className="sk-input"/>
            </FormField>

            <div className="sm:col-span-2">
              <button data-testid="apply-submit" type="submit" disabled={submitting} className="sk-btn-gold w-full disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit application"}
              </button>
              <p className="text-white/40 text-xs mt-3 text-center">By submitting you agree to our terms. We'll never share your details.</p>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="sk-card-dark p-7 sticky top-24">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Live preview</div>
            <div className="mt-3 font-display text-2xl text-white">{form.loan_type} Loan</div>
            <div className="sk-divider my-5"/>
            <div className="grid grid-cols-2 gap-y-4 text-sm">
              <div className="text-white/50">Amount</div><div className="text-white text-right">{INR(+form.amount || 0)}</div>
              <div className="text-white/50">Tenure</div><div className="text-white text-right">{form.tenure_months} months</div>
              <div className="text-white/50">Rate (from)</div><div className="text-white text-right">{rate}% p.a.</div>
            </div>
            <div className="mt-6 p-5 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/25">
              <div className="text-xs text-white/60 uppercase tracking-widest">Estimated EMI</div>
              <div className="font-display text-4xl text-[#D4AF37] mt-1">{INR(emi)}</div>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0"/> Approval within 24 hours</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0"/> Minimal paperwork</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0"/> No hidden charges</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </label>
  );
}
